import type { PermissionKey } from "@/types";

export interface PermissionDef {
  key: PermissionKey;
  label: string;
  description: string;
}

export interface PermissionGroup {
  key: string;
  label: string;
  icon: string;
  tone: string;
  permissions: PermissionDef[];
}

/**
 * The full set of permissions a branch employee can be granted.
 * The job title (role) is descriptive only and grants nothing automatically;
 * the store owner selects the actual permissions manually for every employee.
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: "orders",
    label: "الطلبات",
    icon: "ri-file-list-3-line",
    tone: "bg-primary-100 text-primary-700",
    permissions: [
      {
        key: "orders.receive",
        label: "استقبال طلبات الفرع",
        description: "مراجعة وقبول الطلبات الواردة إلى الفرع",
      },
      {
        key: "orders.updateStatus",
        label: "تحديث حالات الطلبات",
        description: "تغيير حالة الطلب بين جديد وجارٍ التحضير وجاهز ومُستلم",
      },
    ],
  },
  {
    key: "pos",
    label: "نقطة البيع",
    icon: "ri-shopping-cart-2-line",
    tone: "bg-accent-100 text-accent-800",
    permissions: [
      {
        key: "pos.createOrder",
        label: "إدخال طلبات نقطة البيع POS وإصدار الفاتورة",
        description: "إنشاء طلب جديد وإصدار فاتورة من كاشير الفرع",
      },
    ],
  },
  {
    key: "products",
    label: "المنتجات",
    icon: "ri-restaurant-2-line",
    tone: "bg-secondary-200 text-secondary-800",
    permissions: [
      {
        key: "products.availability",
        label: "التحكم في توفر المنتجات",
        description: "إظهار أو إخفاء منتج من قائمة الفرع",
      },
      {
        key: "products.stock",
        label: "تعديل الكميات المتاحة",
        description: "تحديث كمية المخزون المتاحة للبيع",
      },
    ],
  },
  {
    key: "customers",
    label: "العملاء",
    icon: "ri-user-heart-line",
    tone: "bg-primary-100 text-primary-700",
    permissions: [
      {
        key: "customers.rate",
        label: "تقييم العملاء بعد إتمام الطلب",
        description: "إضافة تقييم للعميل بعد إتمام الطلب",
      },
    ],
  },
  {
    key: "branch",
    label: "إعدادات الفرع",
    icon: "ri-settings-4-line",
    tone: "bg-accent-100 text-accent-800",
    permissions: [
      {
        key: "branch.hours",
        label: "تعديل أوقات عمل الفرع",
        description: "تحديث ساعات فتح وإغلاق الفرع",
      },
      {
        key: "branch.pickupPolicy",
        label: "تعديل سياسة الاستلام",
        description: "التحكم بطرق الاستلام من داخل الفرع أو السيارة أو المحلي",
      },
      {
        key: "branch.emergency",
        label: "الفتح والإغلاق الطارئ للفرع",
        description: "إغلاق أو فتح الفرع فورًا عند الحاجة",
      },
    ],
  },
  {
    key: "reports",
    label: "التقارير",
    icon: "ri-bar-chart-box-line",
    tone: "bg-secondary-200 text-secondary-800",
    permissions: [
      {
        key: "reports.branchPerformance",
        label: "عرض تقارير أداء الفرع",
        description: "الاطلاع على مبيعات وطلبات الفرع وأدائها",
      },
    ],
  },
];

/** Flat list of every permission the store owner can grant. */
export const ALL_PERMISSIONS: PermissionKey[] = PERMISSION_GROUPS.flatMap(
  (group) => group.permissions.map((permission) => permission.key),
);